import './HoursSummary.css'

import classNames from 'classnames'
import ToggleSectionButton from 'components/ToggleSectionButton'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DEFAULT_CURRENCY, formatNumberWithCurrency, getMomentDate } from 'utils'

import Box from '@mui/material/Box'

interface RevenuePerHour {
  hour: number
  ordersValueShare: number
  revenueShare: number
  value: number
}

interface Country {
  countryId: number
  countryName: string
  ordersValue?: number | null
  revenue?: number
  revenuePerHour: RevenuePerHour[]
}

interface Store {
  storeId: number
  storeName: string
  storeChannel: string
  ordersValue?: number | null
  revenue?: number
  revenuePerHour: RevenuePerHour[]
}

interface Props {
  currency?: string
  rankingHourlyCountries: {
    date: string
    countries?: Country[]
    stores?: Store[]
    weekDay: number
  }[]
  showOrders: boolean
}

function HoursSummary({ currency = DEFAULT_CURRENCY, rankingHourlyCountries, showOrders = false }: Props) {
  const { t: translate } = useTranslation()
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [countryA, countryB] = rankingHourlyCountries[0]?.countries || []
  const [storeA, storeB] = rankingHourlyCountries[0]?.stores || []
  const nameA = countryA?.countryName || storeA?.storeName || null
  const nameB = countryB?.countryName || storeB?.storeName || null
  const hasBothCountries = Boolean(nameA && nameB)
  const onToggleClick = useCallback(() => setIsCollapsed(!isCollapsed), [isCollapsed, setIsCollapsed])

  return (
    <Box className="hours-summary">
      <table>
        <thead>
          <tr>
            <th>
              <ToggleSectionButton isCollapsed={isCollapsed} onClick={onToggleClick} />
            </th>
            {isCollapsed ? (
              <th className="collapsed-summary">{translate(showOrders ? 'rankings.ordersPerDay' : 'rankings.revenuePerDay')}</th>
            ) : (
              <>
                <th />
                {nameA && <th className={classNames('header-country-name', { both: hasBothCountries })}>{nameA}</th>}
                {nameB && <th className="header-country-name">{nameB}</th>}
              </>
            )}
          </tr>
        </thead>
        {!isCollapsed && rankingHourlyCountries.length > 0 && (
          <tbody>
            {rankingHourlyCountries.map(({ date, countries, stores }) => {
              const valueA = (countries && countries[0]) || (stores && stores[0]) || { ordersValue: null, revenue: null }
              const valueB = (countries && countries[1]) || (stores && stores[1]) || { ordersValue: null, revenue: null }

              return (
                <tr key={date}>
                  <td className="day day-label">{getMomentDate(date).format('ddd')}</td>
                  <td className="day">{getMomentDate(date).format('DD.MM')}</td>
                  {nameA && (
                    <td className={classNames('cell-value', { both: hasBothCountries })}>
                      {formatNumberWithCurrency({
                        currency,
                        number: showOrders ? valueA.ordersValue : valueA.revenue,
                        translate,
                      })}
                    </td>
                  )}
                  {nameB && (
                    <td className={classNames('cell-value', { both: hasBothCountries })}>
                      {formatNumberWithCurrency({
                        currency,
                        number: showOrders ? valueB.ordersValue : valueB.revenue,
                        translate,
                      })}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        )}
      </table>
    </Box>
  )
}

export default HoursSummary
